import React from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  LogOut 
} from 'lucide-react';
import { CommonLabel } from '@/components/ui/common-label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';

export function ProfileMenu({ theme, onLogout }) {
  const { toast } = useToast();
  const { userProfile } = useAuth();

  const iconButtonClasses = 
    `rounded-full p-0 h-9 w-9 shadow-[0px_4.9px_19.6px_0px_#6A73811F] border border-border relative focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0`;

  
  const getUserInitials = (name) => {
    if (!name) return 'AD';
    const names = name.split(' ');
    let initials = names[0].substring(0, 1).toUpperCase();
    if (names.length > 1) {
      initials += names[names.length - 1].substring(0, 1).toUpperCase();
    }
    return initials;
  };

  const getRoleBadgeVariant = (role) => {
    if (!role) return 'default';
    
    const roleLower = role.toLowerCase();
    if (roleLower.includes('admin')) return 'admin';
    if (roleLower.includes('transporter')) return 'transporter';
    if (roleLower.includes('distributor')) return 'distributor';
    if (roleLower.includes('retailer')) return 'retailer';
    return 'default';
  };


  const getRoleDisplayName = () => {
    if (userProfile?.role_display) return userProfile.role_display;
    const role = userProfile?.role || userProfile?.user_role || '';
    if (!role) return 'N/A';
    const clean = role.toLowerCase();
    if (clean === 'field_officer') return 'Field Officer';
    if (clean === 'account_head') return 'Account Head';
    if (clean === 'region_head') return 'Region Head';
    if (clean === 'ipl_admin') return 'IPL Admin';
    return role
      .replace('ipl_', '')
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const userName = userProfile?.username || userProfile?.name || 'User';
  const userEmail = userProfile?.email || userProfile?.company_name || 'No email provided';
  const userRole = userProfile?.role || userProfile?.user_role || 'N/A';
  const userInitials = getUserInitials(userName);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="none" className={iconButtonClasses}>
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-primary text-primary-foreground font-medium">
              {userInitials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount theme={theme}>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col">
            <CommonLabel size="sm" weight="semibold" className="text-foreground">
              {userName}
            </CommonLabel>
            <CommonLabel size="sm" weight="normal" className="text-muted-foreground">
              {userEmail}
            </CommonLabel>
            <div className="mt-1">
              <Badge variant={getRoleBadgeVariant(userRole)} className="text-xs">
                {getRoleDisplayName()}
              </Badge>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          className="text-red-500 focus:text-red-500 dark:focus:text-red-400"
          onClick={onLogout}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}